import { seo } from "@/lib/seo";
import { article, breadcrumb, howTo } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";
import type { Article } from "@/lib/content-blocks";

/**
 * Shared `head()` for the long-form article areas (/tutorials, /geo).
 *
 * Schema is chosen per area, not per article. `/tutorials` documents procedures
 * in the product, so a tutorial with 2+ h2 steps emits HowTo. `/geo` is
 * definitional — "What Is Mention Rate?" is a concept, not a procedure — and its
 * h2s are exposition rather than steps, so it always emits TechArticle. Marking
 * those pages HowTo would assert the very entity the /geo split exists to correct
 * (and Google dropped HowTo rich results in 2023, so it buys nothing either way).
 */
export function articleHead(opts: {
  /** The resolved article, or undefined when the slug doesn't exist. */
  item: Article | undefined;
  slug: string;
  /** Path prefix, e.g. `/geo`. */
  base: string;
  /** Breadcrumb label for the area — spelled out, not abbreviated. */
  areaName: string;
  /** Used in the not-found title, e.g. "Tutorial" -> "Tutorial not found". */
  noun: string;
  /** `"howto"` lets step-shaped articles emit HowTo; `"article"` never does. */
  schema: "howto" | "article";
}) {
  const { item, slug, base, areaName, noun, schema: schemaMode } = opts;

  if (!item) {
    return seo({
      title: `${noun} not found`,
      description: `The ${noun.toLowerCase()} you're looking for doesn't exist or has been moved.`,
      path: `${base}/${slug}`,
      noindex: true,
    });
  }

  const path = `${base}/${item.slug}`;
  const url = `${SITE_URL}${path}`;
  const steps = schemaMode === "howto" ? howToSteps(item) : [];

  const schema =
    steps.length >= 2
      ? howTo({ name: item.title, description: item.intro, steps })
      : article({
          headline: item.title,
          description: item.metaDescription,
          url,
          type: "TechArticle",
        });

  return seo({
    title: item.metaTitle,
    description: item.metaDescription,
    path,
    type: "article",
    jsonLd: [
      schema,
      breadcrumb([
        { name: "Home", url: "/" },
        { name: areaName, url: base },
        { name: item.title, url: path },
      ]),
    ],
  });
}

/** Each h2 becomes a step; its text is the prose that follows it, up to the next h2. */
function howToSteps(item: Article): { name: string; text: string }[] {
  const steps: { name: string; text: string }[] = [];
  let current: { name: string; parts: string[] } | null = null;

  for (const block of item.blocks) {
    if (block.type === "h2") {
      if (current) steps.push(toStep(current));
      current = { name: block.text, parts: [] };
    } else if (current && "text" in block && block.text) {
      current.parts.push(block.text);
    }
  }
  if (current) steps.push(toStep(current));

  return steps;
}

function toStep({ name, parts }: { name: string; parts: string[] }) {
  return { name, text: parts.join(" ").slice(0, 500) || name };
}
